import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Container,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getNewsList } from './NewsApi';

const colors = {
    text: '#1a1a1a',
    primary: '#005a8d',
    primaryDark: '#004870',
    primaryLight: '#f5f9fc',
    border: '#e7edf2',
    muted: '#555555',
};

const CATEGORY_LABEL = {
    notice: '공지사항',
    press: '보도자료',
};

const CATEGORY_COLOR = {
    notice: 'primary',
    press: 'success',
};

const getCategoryLabel = (category) => CATEGORY_LABEL[category] || '?뚮┝';

const normalizeNewsList = (payload) => {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

const formatDateString = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}.${m}.${d}`;
};

function News() {
    const navigate = useNavigate();
    const [newsList, setNewsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [category, setCategory] = useState('all');

    useEffect(() => {
        let mounted = true;

        const fetchNews = async () => {
            try {
                setLoading(true);
                const res = await getNewsList(category);
                const data = normalizeNewsList(res.data);
                const sorted = [...data].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );

                if (mounted) {
                    setNewsList(sorted);
                    setError('');
                }
            } catch (err) {
                if (mounted) {
                    setError('소식을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchNews();

        return () => {
            mounted = false;
        };
    }, [category]);

    const filteredList = newsList;

    const handleClick = (id) => {
        if (!id) return;
        navigate(`/news/${id}`);
    };

    return (
        <Box sx={{ bgcolor: '#ffffff', color: colors.text, minHeight: '100vh' }}>
            <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: { xs: 4, md: 6 } }}>
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, mb: 1.5, letterSpacing: '-0.02em' }}
                        >
                              공지사항 · 보도자료
                        </Typography>
                        <Typography sx={{ color: colors.muted, wordBreak: 'keep-all' }}>
                            가람마인드의 새로운 소식과 발표 자료를 확인해 보세요.
                        </Typography>
                    </Box>

                    <Tabs
                        value={category}
                        onChange={(e, value) => setCategory(value)}
                        sx={{
                            mb: 4,
                            minHeight: 'auto',
                            '& .MuiTab-root': {
                                minHeight: 'auto',
                                fontWeight: 700,
                                textTransform: 'none',
                                px: 2,
                            },
                            '& .MuiTabs-indicator': { bgcolor: colors.primary },
                        }}
                    >
                        <Tab value="all" label="전체" />
                        <Tab value="notice" label="공지사항" />
                        <Tab value="press" label="보도자료" />
                    </Tabs>

                    {loading && (
                        <Box
                            sx={{
                                minHeight: 260,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Stack alignItems="center" spacing={2}>
                                <CircularProgress sx={{ color: colors.primary }} />
                                <Typography sx={{ color: colors.muted }}>
                                    소식을 불러오고 있습니다.
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{ borderRadius: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && filteredList.length === 0 && (
                        <Box
                            sx={{
                                border: `1px solid ${colors.border}`,
                                borderRadius: 4,
                                bgcolor: colors.primaryLight,
                                py: { xs: 8, md: 10 },
                                px: 3,
                                textAlign: 'center',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                                등록된 소식이 없습니다.
                            </Typography>
                            <Typography sx={{ color: colors.muted }}>
                                새로운 소식이 등록되면 이곳에서 안내드리겠습니다.
                            </Typography>
                        </Box>
                    )}

                    {!loading && !error && filteredList.length > 0 && (
                        <Stack spacing={2}>
                            {filteredList.map((item) => (
                                <Box
                                    key={item.id}
                                    component="article"
                                    onClick={() => handleClick(item.id)}
                                    sx={{
                                        border: `1px solid ${colors.border}`,
                                        borderRadius: 3,
                                        p: { xs: 2.5, md: 3 },
                                        cursor: 'pointer',
                                        bgcolor: '#ffffff',
                                        transition:
                                            'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            borderColor: 'rgba(0, 90, 141, 0.35)',
                                            boxShadow: '0 14px 28px rgba(15, 23, 42, 0.06)',
                                        },
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        spacing={1.5}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                            sx={{ minWidth: 0 }}
                                        >
                                            <Chip
                                                label={getCategoryLabel(item.category)}
                                                color={CATEGORY_COLOR[item.category] || 'default'}
                                                size="small"
                                                sx={{ fontWeight: 800, flexShrink: 0 }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '1.05rem',
                                                    wordBreak: 'keep-all',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: { xs: 'normal', sm: 'nowrap' },
                                                }}
                                            >
                                                {item.title || '?쒕ぉ 誘몄젙'}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            sx={{
                                                color: colors.muted,
                                                fontSize: '0.9rem',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {formatDateString(item.created_at)}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Container>
            </Box>
        </Box>
    );
}

export default News;


